import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useMissatgeError } from '@/api/errors';
import {
  useDepartamentsAdmin,
  useRols,
  useSyncEntra,
  useUsuarisAdmin,
  type FiltreUsuaris,
  type RolDto,
} from '@/api/administracio';
import { useUsuarisPerRol, type UsuariPerfilDto } from '@/api/ajustosPersones';
import { Desplegable } from '@/components/Desplegable';
import { Icona } from '@/components/Icona';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Avatar, Buit } from '@/pages/operacio/comu';
import { Permis } from '@/permisos/Permis';
import { Cerca, FaTemps } from './comu';
import { AltaPersona } from './persones/AltaPersona';
import './administracio.css';
import './persones.css';

export function W20Usuaris() {
  const { t, i18n } = useTranslation('administracio');
  const navigate = useNavigate();
  const missatgeError = useMissatgeError();
  const [filtre, setFiltre] = useState<FiltreUsuaris>({ text: '', actiu: '', page: 0 });
  const [senseRol, setSenseRol] = useState(false);
  const [modificats, setModificats] = useState(false);
  const [alta, setAlta] = useState(false);
  const usuaris = useUsuarisAdmin(filtre);
  const rols = useRols();
  const departaments = useDepartamentsAdmin();
  const sync = useSyncEntra();
  const perRol = useUsuarisPerRol(rols.data ?? []);
  const canvia = (p: Partial<FiltreUsuaris>) => setFiltre((v) => ({ ...v, page: 0, ...p }));
  const pagina = usuaris.data;
  const es = i18n.language === 'es';
  const nomRol = (r?: RolDto) => (r ? (es ? r.nomEs : r.nomCa) : '');
  const nomDep = (id?: number | null) => departaments.data?.find((d) => d.id === id)?.nom ?? '—';
  const rolsDe = (id: number) =>
    (perRol.perUsuari.get(id) ?? [])
      .map((rid) => rols.data?.find((r) => r.id === rid))
      .filter((r): r is RolDto => !!r);

  // Filtres ràpids: s'apliquen sobre la pàgina carregada (l'API no els ofereix).
  const files = useMemo(
    () =>
      ((pagina?.items ?? []) as UsuariPerfilDto[]).filter(
        (u) =>
          (!senseRol || (perRol.carregat && !perRol.perUsuari.has(u.id))) &&
          (!modificats || (u.campsLocals?.length ?? 0) > 0),
      ),
    [pagina, senseRol, modificats, perRol],
  );

  return (
    <section className="pantalla" data-screen="W-20">
      <Toolbar titol={t('titols.usuaris')}>
        <span className="t-caption dim persones-nota-sync">
          {t('persones.usuaris.notaSyncGlobal')}
        </span>
        <Permis recurs="cai.usuaris" tipus="MODIFICAR">
          <button
            type="button"
            className="btn btn--outline btn--s"
            disabled={sync.isPending}
            onClick={() => sync.mutate()}
          >
            <Icona nom="arrow-clockwise" />
            {sync.isPending ? t('usuaris.sincronitzant') : t('persones.usuaris.syncGlobal')}
          </button>
        </Permis>
        <Permis recurs="cai.usuaris" tipus="ALTA">
          <button type="button" className="btn btn--dark btn--s" onClick={() => setAlta(true)}>
            <Icona nom="plus" />
            {t('altaPersona.boto')}
          </button>
        </Permis>
      </Toolbar>
      {alta ? (
        <AltaPersona
          onTanca={() => setAlta(false)}
          onCreat={(id) => navigate(`/admin/usuaris/${id}`)}
        />
      ) : null}
      <div className="content stack">
        {sync.data ? (
          <div className="info-banda" role="status">
            <Icona nom="checkmark-circle" />
            {t('usuaris.resultatSync', { ...sync.data })}
          </div>
        ) : null}
        {sync.isError ? <p className="error-text">{missatgeError(sync.error)}</p> : null}
        <div className="admin-filtres persones-filtres">
          <Cerca
            className="persones-filtres__cerca"
            valor={filtre.text ?? ''}
            onCanvia={(text) => canvia({ text })}
            placeholder={t('usuaris.cerca')}
          />
          <Desplegable
            variant="chip"
            aria-label={t('usuaris.departament')}
            actiu={!!filtre.departamentId}
            valor={String(filtre.departamentId ?? '')}
            onCanvia={(v) => canvia({ departamentId: v === '' ? '' : Number(v) })}
            opcions={[
              { valor: '', etiqueta: t('persones.usuaris.tots') },
              ...(departaments.data ?? []).map((d) => ({ valor: String(d.id), etiqueta: d.nom })),
            ]}
          />
          <Desplegable
            variant="chip"
            aria-label={t('usuaris.rol')}
            actiu={!!filtre.rolId}
            valor={String(filtre.rolId ?? '')}
            onCanvia={(v) => canvia({ rolId: v === '' ? '' : Number(v) })}
            opcions={[
              { valor: '', etiqueta: t('persones.usuaris.totsRols') },
              ...(rols.data ?? [])
                .filter((r) => r.id != null)
                .map((r) => ({ valor: String(r.id), etiqueta: nomRol(r) })),
            ]}
          />
          <Desplegable
            variant="chip"
            aria-label={t('usuaris.estat')}
            actiu={filtre.actiu !== ''}
            valor={filtre.actiu === '' || filtre.actiu == null ? '' : String(filtre.actiu)}
            onCanvia={(v) => canvia({ actiu: v === '' ? '' : v === 'true' })}
            opcions={[
              { valor: '', etiqueta: t('persones.usuaris.totsEstats') },
              { valor: 'true', etiqueta: t('persones.usuaris.actius') },
              { valor: 'false', etiqueta: t('persones.usuaris.inactius') },
            ]}
          />
          <button
            type="button"
            className="chip"
            aria-pressed={senseRol}
            title={t('persones.usuaris.notaFiltresRapids')}
            onClick={() => setSenseRol((v) => !v)}
          >
            {t('persones.usuaris.filtreSenseRol')}
          </button>
          <button
            type="button"
            className="chip"
            aria-pressed={modificats}
            title={t('persones.usuaris.notaFiltresRapids')}
            onClick={() => setModificats((v) => !v)}
          >
            <Icona nom="pencil" />
            {t('persones.usuaris.filtreModificats')}
          </button>
        </div>
        <div className="panel">
          {usuaris.isError ? (
            <ErrorCarrega onReintenta={() => void usuaris.refetch()} />
          ) : !pagina || files.length === 0 ? (
            <Buit icona="person-3">
              {usuaris.isLoading ? t('comu.carregant') : t('usuaris.buit')}
            </Buit>
          ) : (
            <div className="taula-scroll">
              <table className="taula taula--clicable">
                <thead>
                  <tr>
                    <th>{t('usuaris.persona')}</th>
                    <th>{t('usuaris.departament')}</th>
                    <th>{t('persones.usuaris.accessos')}</th>
                    <th>{t('usuaris.estat')}</th>
                    <th>{t('usuaris.ultimSync')}</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((u) => {
                    const nom = `${u.nom} ${u.cognoms ?? ''}`.trim();
                    const rolsU = rolsDe(u.id);
                    const locals = u.campsLocals?.length ?? 0;
                    return (
                      <tr key={u.id} onClick={() => navigate(`/admin/usuaris/${u.id}`)}>
                        <td>
                          <Link
                            to={`/admin/usuaris/${u.id}`}
                            className="hstack persones-persona"
                            aria-label={t('persones.usuaris.obreFitxa', { nom })}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Avatar nom={nom} />
                            <span className="persones-persona__text">
                              <span className="strong">
                                {nom}
                                {locals > 0 ? (
                                  <span
                                    className="badge badge--incident persones-marca"
                                    title={t('persones.usuaris.modificatCai')}
                                    aria-label={t('persones.usuaris.modificatCai')}
                                  >
                                    <Icona nom="pencil" />
                                  </span>
                                ) : null}
                              </span>
                              <span className="t-caption dim">{u.carrec ?? u.upn ?? u.email}</span>
                            </span>
                          </Link>
                        </td>
                        <td>{nomDep(u.departamentId)}</td>
                        <td>
                          {!perRol.carregat ? (
                            <span className="dim">…</span>
                          ) : rolsU.length === 0 ? (
                            <span className="badge badge--incident">
                              <Icona nom="exclamationmark-triangle" />
                              {t('persones.usuaris.senseRol')}
                            </span>
                          ) : (
                            <span className="persones-rols">
                              {rolsU.map((r) => (
                                <span key={r.id} className="badge badge--preventiu">
                                  {nomRol(r)}
                                </span>
                              ))}
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={`badge badge--${u.actiu ? 'ok' : 'gray'}`}>
                            {u.actiu ? t('comu.actiu') : t('comu.inactiu')}
                          </span>
                        </td>
                        <td className="dim">
                          <FaTemps iso={u.ultimSync} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {pagina && pagina.total > pagina.size ? (
            <div className="panel__foot">
              <span className="t-caption dim">{t('usuaris.total', { count: pagina.total })}</span>
              <div className="spacer" />
              <button
                type="button"
                className="btn btn--xs"
                disabled={(filtre.page ?? 0) === 0}
                onClick={() => setFiltre((v) => ({ ...v, page: (v.page ?? 0) - 1 }))}
              >
                {t('comu.anterior')}
              </button>
              <span className="t-caption dim">
                {t('comu.pagina', {
                  actual: (filtre.page ?? 0) + 1,
                  total: Math.ceil(pagina.total / pagina.size),
                })}
              </span>
              <button
                type="button"
                className="btn btn--xs"
                disabled={((filtre.page ?? 0) + 1) * pagina.size >= pagina.total}
                onClick={() => setFiltre((v) => ({ ...v, page: (v.page ?? 0) + 1 }))}
              >
                {t('comu.seguent')}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
